// What a campaign's sync status means, as pure functions over the response.
//
// These lived in `api/manage-hooks.ts`, which is the wrong home the moment a
// second reader appears: `lib/campaign-publish-sequence.ts` is a pure module
// and importing the hooks file would drag react-query into it. So they moved
// here and `manage-hooks` re-exports them — one definition, three consumers.
//
// That matters more than tidiness. The publish sequence originally derived its
// final step from `provider_state` alone while the modal derived its verdict
// from `campaignPublishOutcome`, and the two disagreed about exactly the cases
// that are hard to see: a failed block, a row for the wrong destination, and
// the deliberate both-null local-ready mode. Two correct-looking derivations
// that merely disagree is the shape of `436db20`, which shipped green and
// wedged every Meta audience for three days.

import type {
  CampaignProviderState,
  CampaignPublishOutcome,
  CampaignSyncBlock,
  CampaignSyncStatus,
  CampaignSyncTarget,
  ManageCampaignPlan,
} from '@/api/manage-types';

/**
 * A creative slot change or roll-out the queue is executing right now: its
 * journal rows are `running` under `slot_*` / `rollout_*` keys. The sync
 * status polls at the urgent cadence while this is true, so the card's
 * per-audience steps move as the consumer writes them (2026-09-18).
 */
export function campaignLaterLaneInFlight(status: CampaignSyncStatus): boolean {
  return status.stages.some(
    (stage) => stage.status === 'running' && /^(?:slot|rollout)_/.test(stage.stage_key),
  );
}

/**
 * How many creative operations (slot changes and roll-outs) are queued or
 * running on a plan. The plan resource polls while this is above zero, and
 * the editor header shows "{n} change(s) in progress". Type-only inputs so
 * the resource registry can import it without a cycle.
 */
export function countInFlightOperations(
  plan: Pick<ManageCampaignPlan, 'creative_slot_changes' | 'creative_rollouts'> | null | undefined,
): number {
  if (!plan) return 0;
  const inFlight = (status: string) => status === 'queued' || status === 'running';
  return (plan.creative_slot_changes ?? []).filter((row) => inFlight(row.status)).length
    + (plan.creative_rollouts ?? []).filter((row) => inFlight(row.status)).length;
}

export function terminalSyncBlock(block: CampaignSyncBlock): boolean {
  return block.status === 'synced' || block.status === 'failed';
}

/**
 * The campaign-level verdict for one destination.
 *
 * Note the asymmetry in the null case, which is deliberate and load-bearing:
 * no rows at all is the intentional local-ready mode, but a row for only the
 * *other* destination is a backend invariant failure and must never be
 * mistaken for a successful dark launch.
 */
export function campaignPublishOutcome(
  status: CampaignSyncStatus,
  target: CampaignSyncTarget,
): CampaignPublishOutcome {
  const expected = status[target];
  const other = status[target === 'meta' ? 'ttd' : 'meta'];
  if (expected === null) {
    return other === null ? 'local_ready' : 'failed';
  }
  if (expected.status === 'synced') return 'synced';
  if (expected.status === 'failed') return 'failed';
  return 'pending';
}

export function campaignSyncTerminal(
  status: CampaignSyncStatus,
  target?: CampaignSyncTarget,
): boolean {
  if (target) {
    return campaignPublishOutcome(status, target) !== 'pending';
  }
  const blocks = [status.meta, status.ttd].filter(
    (block): block is CampaignSyncBlock => block !== null,
  );
  return blocks.length === 0 || blocks.every(terminalSyncBlock);
}

/**
 * Provider states that nothing will move on its own.
 *
 * The two that are missing are the point. `active_pending_delivery` becomes
 * `active` when the provider counts the first impression, and
 * `scheduled_paused` becomes active when a flight starts covering today —
 * both by way of the per-minute `flight_reconcile` cron, with no user action
 * and no further write from this browser.
 */
const SETTLED_PROVIDER_STATES = new Set<CampaignProviderState>([
  'active',
  'paused_superseded',
  'disabled',
  'disabled_superseded',
]);

/**
 * Has this campaign stopped changing, for polling purposes?
 *
 * Strictly narrower than [`campaignSyncTerminal`], and the two answer different
 * questions. Terminal means "the publish finished, stop showing a spinner on
 * the launch button" — the modal is right to use it. Settled means "nothing
 * will change if we stop asking", which is what a poll cadence needs.
 *
 * They were the same predicate until the right column began reporting the path
 * to serving continuously. A campaign synced to Meta with
 * `active_pending_delivery` is terminal, so polling stopped — and the Serving
 * step then sat on "live at the provider; first impressions not counted yet"
 * for the life of the tab, because the reconciler's eventual `active` was
 * never fetched. `refetchOnWindowFocus` is off in this app, so nothing else
 * would have rescued it either.
 *
 * A block with no `provider_state` at all counts as settled: the field is
 * absent only on responses predating it, and polling forever on a response
 * shape we cannot interpret is worse than stopping.
 */
export function campaignSyncSettled(
  status: CampaignSyncStatus,
  target?: CampaignSyncTarget,
): boolean {
  if (!campaignSyncTerminal(status, target)) return false;
  const blocks = target
    ? [status[target]]
    : [status.meta, status.ttd];
  return blocks
    .filter((block): block is CampaignSyncBlock => block != null)
    .every(
      (block) =>
        block.status !== 'synced' ||
        block.provider_state === undefined ||
        SETTLED_PROVIDER_STATES.has(block.provider_state),
    );
}
